// Result message the SCORM demo package wrapper listens for (see mple-nestjs
// src/scorm/scorm-demo-package.service.ts). The wrapper owns the LMS API; this
// page only tells it the final outcome, once.

/** Must match SCORM_MESSAGE_SOURCE in mple-nestjs. */
export const SCORM_MESSAGE_SOURCE = "mple-scorm-result";

/** sessionStorage keys a SCORM SSO login sets (pages/index.tsx). */
export const SCORM_STORAGE_KEYS = {
  isScormLogin: "isScormLogin",
  // Module URL the learner lands on; the _app guard sends them back to it.
  launchUrl: "scormLaunchUrl",
  // Package's module and login time: only that module's attempt started
  // after login is reported.
  moduleId: "scormModuleId",
  loginAt: "scormLoginAt",
} as const;

export type ScormResultMessage = {
  source: typeof SCORM_MESSAGE_SOURCE;
  // Demo package only; the SSO package matches on origin and source.
  sessionKey?: string;
  lessonStatus: "passed" | "failed" | "completed";
  score?: number;
  sessionTime?: number;
};

/**
 * Builds the message for a finished result, or null while the result is not
 * final yet (or errored, which leaves the LMS status at "incomplete").
 *
 * The pass/fail decision comes from the backend: a roleplay it marked
 * `failed` reports "failed". A `completed` roleplay reports "passed" when it
 * has a passing score and cleared it, otherwise "completed". Coach and bot
 * sessions are never scored and report "completed".
 */
export const buildScormResultMessage = (
  result: any,
  sessionKey?: string
): ScormResultMessage | null => {
  const status = result?.status;
  if (status !== "completed" && status !== "failed") {
    return null;
  }

  const seconds = Number(result?.mediaLength || result?.sessionDuration / 1000);
  const sessionTime =
    Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
  const base = {
    source: SCORM_MESSAGE_SOURCE,
    sessionKey,
    sessionTime,
  } as const;

  if (["bot", "coach"].includes(result?.type?.toLowerCase())) {
    return { ...base, lessonStatus: "completed" };
  }

  const isScored =
    result?.withoutScoring !== true &&
    result?.roleplayDetails?.withoutScoring !== true;
  const score = Number(result?.result?.totalPercentage);
  const hasScore = isScored && Number.isFinite(score);

  if (status === "failed") {
    return { ...base, lessonStatus: "failed", ...(hasScore && { score }) };
  }

  const passingScore = Number(result?.passingScore);
  const passed =
    hasScore && Number.isFinite(passingScore) && passingScore > 0
      ? score >= passingScore
      : false;

  return {
    ...base,
    lessonStatus: passed ? "passed" : "completed",
    ...(hasScore && { score }),
  };
};
